import React, { useState, useEffect, useMemo } from 'react';
import SavedExitLevels from './SavedExitLevels';
import NotificationSettingsModal from './NotificationSettingsModal';
import { parseFloatFromString } from '../utils/formatters';

const ExitStrategySection = ({
  assetKind = 'crypto',
  position,
  token,
  portfolioId,
  updateCache,
  refetch,
  onEdit,
  currency
}) => {
  const getStoredCollapseState = (sectionName, defaultValue = false) => {
    try {
      const storedValue = localStorage.getItem(`${sectionName}_collapsed`);
      return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  };

  const [collapsed, setCollapsed] = useState(() => getStoredCollapseState('exitStrategy', false));
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem('exitStrategy_collapsed', JSON.stringify(collapsed));
    } catch (_) {}
  }, [collapsed]);

  // Memoize exit levels to avoid dependency issues
  const exitLevels = useMemo(() => {
    return position?.exitStrategy?.levels || [];
  }, [position?.exitStrategy?.levels]);
  
  const hasLevels = exitLevels.length > 0;
  const isModified = position?.exitStrategy?.isModified || false;
  const templateName = position?.exitStrategy?.templateName;
  const templateOutOfSync = position?.exitStrategy?.templateOutOfSync || false;

  const plannedSellPct = useMemo(() => {
    if (!position?.exitStrategy?.sellAmount || !position?.holdingsAmount) return null;
    const sell = parseFloatFromString(position.exitStrategy.sellAmount);
    const total = parseFloatFromString(position.holdingsAmount);
    if (!total || total <= 0) return null;
    return Math.min(100, Math.max(0, (sell / total) * 100)).toFixed(1);
  }, [position?.exitStrategy?.sellAmount, position?.holdingsAmount]);

  // Get current price from token market data
  const currentPrice = parseFloatFromString(token?.marketData?.currentPrice || '0');

  // Calculate price status indicator
  // Red if ANY non-filled level has price above it, yellow if ANY is within 10%
  const priceStatus = useMemo(() => {
    if (!hasLevels || currentPrice <= 0) return null;
    
    const nonFilledLevels = exitLevels.filter(l => (l.status || 'PLANNED').toUpperCase() !== 'FILLED');
    if (nonFilledLevels.length === 0) return null;
    
    let hasRed = false;
    let hasYellow = false;
    
    for (const level of nonFilledLevels) {
      const levelPrice = parseFloatFromString(level.price);
      if (currentPrice > levelPrice) {
        hasRed = true;
        break; // Red takes priority, no need to check further
      } else if (currentPrice >= levelPrice * 0.9) {
        hasYellow = true;
      }
    }
    
    if (hasRed) return 'red';
    if (hasYellow) return 'yellow';
    return null;
  }, [exitLevels, currentPrice, hasLevels]);

  // Calculate target ROI multiple
  const targetMultiple = useMemo(() => {
    if (!position?.planned?.multiple) return null;
    return position.planned.multiple;
  }, [position?.planned?.multiple]);

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-md mb-4 overflow-hidden border border-gray-200 dark:border-gray-700">
      <div 
        className="flex justify-between items-center py-2 px-3 cursor-pointer bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600"
        onClick={() => setCollapsed(!collapsed)}
      >
        <h2 className="font-semibold text-base flex items-center flex-wrap gap-y-1">
          <span className="flex items-center">
            Exit Strategy
            {hasLevels && (
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                — {templateName || 'Custom'}
                {isModified && (
                  <span className="ml-1 text-xs text-orange-500 dark:text-orange-400" title="Levels have been manually edited since the strategy was applied">(Modified)</span>
                )}
                {templateOutOfSync && templateName && !isModified && (
                  <span className="ml-1" title="The strategy template has been updated since it was applied">
                    <svg className="w-4 h-4 text-blue-500 dark:text-blue-400 inline" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </span>
            )}
            {!hasLevels && (
              <span className="ml-2 text-sm font-normal text-gray-400">— Not set</span>
            )}
            {/* Price status indicator */}
            {priceStatus === 'red' && (
              <span 
                className="ml-2 w-3 h-3 rounded-full bg-red-500 inline-block" 
                title="Price exceeds all remaining exit levels"
              />
            )}
            {priceStatus === 'yellow' && (
              <span 
                className="ml-2 w-3 h-3 rounded-full bg-yellow-500 inline-block" 
                title="Price within 10% of next exit level"
              />
            )}
          </span>
          {hasLevels && (plannedSellPct || targetMultiple) && (
            <span className="text-sm font-normal ml-3 text-blue-600 dark:text-blue-400" title="Planned exit strategy summary">
              {plannedSellPct && (
                <>{plannedSellPct}% of holdings</>
              )}
              {targetMultiple && (
                <>
                  {plannedSellPct && <span className="mx-2">•</span>}
                  {targetMultiple}x target
                </>
              )}
            </span>
          )}
        </h2>
        <div className="flex items-center space-x-2">
          {hasLevels && (
            <button 
              type="button"
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 focus:outline-none"
              onClick={(e) => { e.stopPropagation(); setNotificationModalOpen(true); }}
              title="Notification settings"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
            </button>
          )}
          <button 
            type="button"
            className="bg-blue-500 hover:bg-blue-700 text-white text-xs font-semibold py-1 px-2 rounded"
            onClick={(e) => { e.stopPropagation(); if (typeof onEdit === 'function') onEdit(); }}
          >
            {hasLevels ? 'Edit' : 'Set'}
          </button>
          <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            {collapsed ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>
      </div>
      {!collapsed && (
        <div className="p-3 space-y-4">
          {hasLevels ? (
            <div>
              <SavedExitLevels assetKind={assetKind}
                position={position} 
                token={token} 
                portfolioId={portfolioId} 
                updateCache={updateCache}
                refetch={refetch}
              />
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                Plan your exit levels to lock in profits systematically.
              </p>
              <button
                onClick={(e) => { e.stopPropagation(); if (typeof onEdit === 'function') onEdit(); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Set Exit Strategy
              </button>
            </div>
          )}
        </div>
      )}
      <NotificationSettingsModal 
        isOpen={notificationModalOpen} 
        onClose={() => setNotificationModalOpen(false)} 
      />
    </div>
  );
};

export default ExitStrategySection;
